import { useState, useEffect } from "react";
import { Plus, Trash2, Save, Mail, MessageCircle, ShieldCheck, User as UserIcon, Loader2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { listRecipients, upsertRecipient, deleteRecipient } from "@/lib/api";

const empty = {
  id: null, name: "", role: "assistant", email: "", whatsapp: "",
  notify_lead: true, notify_decline: true, notify_digest: false, can_login: true,
};

export default function RecipientsTab() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      setItems(await listRecipients());
    } catch (e) {
      const msg = e?.response?.status === 401
        ? "Oturumunuz sona erdi. Lütfen yeniden giriş yapın."
        : (e?.response?.data?.detail || "Liste yüklenemedi. Sayfayı yenileyin.");
      toast.error(msg);
      setItems([]);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.name) return toast.error("İsim gerekli");
    if (!form.email && !form.whatsapp) return toast.error("Email veya WhatsApp gerekli");
    setSaving(true);
    try {
      await upsertRecipient(form);
      toast.success(form.id ? "Güncellendi" : "Eklendi");
      setForm(empty);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Hata");
    } finally { setSaving(false); }
  };

  const edit = (r) => setForm({ ...r });
  const del = async (id) => {
    try {
      await deleteRecipient(id);
      toast.success("Silindi");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Hata"); }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="font-display">{form.id ? "Kişiyi Düzenle" : "Yeni Yönetici / Yardımcı Ekle"}</CardTitle>
          <p className="text-xs text-muted-foreground mt-1">
            Eklediğiniz kişiler Google hesaplarıyla yönetici paneline giriş yapabilir ve seçtikleri bildirimleri alır.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">İsim Soyisim</Label>
              <Input data-testid="rec-name" placeholder="Örn: Ayşe Hemşire" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs">Rol</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="rec-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin (tüm yetkiler)</SelectItem>
                  <SelectItem value="assistant">Yardımcı (sadece görüntüleme)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs flex items-center gap-1"><Mail className="w-3 h-3" /> Email (Google hesabı)</Label>
              <Input
                data-testid="rec-email"
                type="email"
                placeholder="ornek@gmail.com"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
            </div>
            <div>
              <Label className="text-xs flex items-center gap-1"><MessageCircle className="w-3 h-3" /> WhatsApp Numarası</Label>
              <Input
                data-testid="rec-whatsapp"
                placeholder="+905551234567"
                value={form.whatsapp}
                onChange={(e) => setForm({ ...form, whatsapp: e.target.value })}
              />
            </div>
          </div>

          <div className="bg-secondary/40 rounded-xl p-4 space-y-3">
            <div className="text-xs uppercase tracking-widest text-muted-foreground font-display mb-1">Bildirim Tercihleri</div>
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-medium">Yeni lead bildirimi</div>
                <div className="text-xs text-muted-foreground">Bir hasta iletişim bilgisi bıraktığında</div>
              </div>
              <Switch data-testid="rec-notify-lead" checked={form.notify_lead} onCheckedChange={(v) => setForm({ ...form, notify_lead: v })} />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-medium">Reddetme bildirimi</div>
                <div className="text-xs text-muted-foreground">Olumsuz konuşma sonrası özet</div>
              </div>
              <Switch data-testid="rec-notify-decline" checked={form.notify_decline} onCheckedChange={(v) => setForm({ ...form, notify_decline: v })} />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-medium">Günlük özet (digest)</div>
                <div className="text-xs text-muted-foreground">Her gün belirlenen saatte AI özeti</div>
              </div>
              <Switch data-testid="rec-notify-digest" checked={form.notify_digest} onCheckedChange={(v) => setForm({ ...form, notify_digest: v })} />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-medium flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> Panele giriş yetkisi</div>
                <div className="text-xs text-muted-foreground">Bu Google hesabıyla admin paneline girebilir</div>
              </div>
              <Switch data-testid="rec-can-login" checked={form.can_login} onCheckedChange={(v) => setForm({ ...form, can_login: v })} />
            </div>
          </div>

          <div className="flex gap-2">
            <Button data-testid="rec-save" onClick={save} disabled={saving} style={{ background: "#B39B7A" }}>
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : (form.id ? <Save className="w-4 h-4 mr-2" /> : <Plus className="w-4 h-4 mr-2" />)}
              {form.id ? "Güncelle" : "Ekle"}
            </Button>
            {form.id && <Button variant="outline" onClick={() => setForm(empty)}><X className="w-4 h-4 mr-2" /> İptal</Button>}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="font-display">Kayıtlı Kişiler ({items.length})</CardTitle></CardHeader>
        <CardContent>
          {loading ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> :
            items.length === 0 ? (
              <div className="text-center text-muted-foreground text-sm py-8">Henüz kişi yok</div>
            ) : (
              <div className="space-y-2">
                {items.map((r) => (
                  <div key={r.id} data-testid={`rec-row-${r.id}`} className="p-4 border border-border/40 rounded-2xl flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3 min-w-0 flex-1">
                      <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
                        style={{ background: r.role === "admin" ? "#2C3531" : "#8B9B88", color: "white" }}>
                        <UserIcon className="w-4 h-4" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <div className="font-medium">{r.name}</div>
                          <Badge variant={r.role === "admin" ? "default" : "secondary"} style={r.role === "admin" ? { background: "#B39B7A" } : {}}>
                            {r.role === "admin" ? "Admin" : "Yardımcı"}
                          </Badge>
                          {r.can_login && <Badge variant="outline" className="text-[10px]"><ShieldCheck className="w-3 h-3 mr-1" />giriş yetkili</Badge>}
                        </div>
                        <div className="text-xs text-muted-foreground mt-1 space-y-0.5">
                          {r.email && <div className="flex items-center gap-1"><Mail className="w-3 h-3" />{r.email}</div>}
                          {r.whatsapp && <div className="flex items-center gap-1"><MessageCircle className="w-3 h-3" />{r.whatsapp}</div>}
                        </div>
                        <div className="flex gap-1 mt-2 flex-wrap">
                          {r.notify_lead && <Badge variant="outline" className="text-[10px]">lead</Badge>}
                          {r.notify_decline && <Badge variant="outline" className="text-[10px]">reddetme</Badge>}
                          {r.notify_digest && <Badge variant="outline" className="text-[10px]">günlük özet</Badge>}
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-1 flex-shrink-0">
                      <Button variant="ghost" size="sm" onClick={() => edit(r)} data-testid={`rec-edit-${r.id}`}>Düzenle</Button>
                      <Button variant="ghost" size="icon" onClick={() => del(r.id)} className="text-destructive" data-testid={`rec-del-${r.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
        </CardContent>
      </Card>
    </div>
  );
}
